import { FormStage, GetKyc } from 'shared/data/walletsKyc/v1';
import type { Paths } from 'type-fest';
import { Path } from 'urlBuilder';

export type FormDataFields = Paths<NonNullable<GetKyc['response']['data']>>;

export enum WizardStep {
    UserInfo = 'userInfo',
    CompanyInfo = 'companyInfo',
    CompanyAddresses = 'companyAddresses',
    CompanySector = 'companySector',
    AccountUsageStep1 = 'accountUsageOne',
    AccountUsageStep2 = 'accountUsageTwo',
    AccountUsageStep3 = 'accountUsageThree',
    PersonsBasic = 'personsBasic',
    PersonsSettings = 'personsSettings',
    PersonsDetails = 'personsDetails',
    AdditionalDocuments = 'additionalDocuments',
    Submit = 'submit',
}

export const wizardSteps = <const>[
    // WizardStep.UserInfo,
    WizardStep.CompanyInfo,
    WizardStep.CompanyAddresses,
    WizardStep.CompanySector,
    WizardStep.AccountUsageStep1,
    WizardStep.AccountUsageStep2,
    WizardStep.AccountUsageStep3,
    WizardStep.PersonsBasic,
    WizardStep.PersonsSettings,
    WizardStep.PersonsDetails,
    WizardStep.AdditionalDocuments,
    WizardStep.Submit,
];

export enum StepperStep {
    UserInformation = 'userInformation',
    CompanyInformation = 'companyInformation',
    AccountUsage = 'accountUsage',
    Persons = 'persons',
    AdditionalDocuments = 'additionalDocuments',
    Submit = 'submit',
}

export const stepperSteps = <const>[
    // StepperStep.UserInformation,
    StepperStep.CompanyInformation,
    StepperStep.AccountUsage,
    StepperStep.Persons,
    StepperStep.AdditionalDocuments,
    StepperStep.Submit,
];

export const wizardStepToStepperStepMap = <const>{
    // [WizardStep.UserInfo]: StepperStep.UserInformation,
    [WizardStep.CompanyInfo]: StepperStep.CompanyInformation,
    [WizardStep.CompanyAddresses]: StepperStep.CompanyInformation,
    [WizardStep.CompanySector]: StepperStep.CompanyInformation,
    [WizardStep.AccountUsageStep1]: StepperStep.AccountUsage,
    [WizardStep.AccountUsageStep2]: StepperStep.AccountUsage,
    [WizardStep.AccountUsageStep3]: StepperStep.AccountUsage,
    [WizardStep.PersonsBasic]: StepperStep.Persons,
    [WizardStep.PersonsSettings]: StepperStep.Persons,
    [WizardStep.PersonsDetails]: StepperStep.Persons,
    [WizardStep.AdditionalDocuments]: StepperStep.AdditionalDocuments,
    [WizardStep.Submit]: StepperStep.Submit,
};

// TODO: this should be generated dynamically and should take into
//  account last filled sub-step in a step
export const stepperStepsToWizardStepMap = {
    // [StepperStep.UserInformation]: WizardStep.UserInfo,
    [StepperStep.CompanyInformation]: WizardStep.CompanyInfo,
    [StepperStep.AccountUsage]: WizardStep.AccountUsageStep1,
    [StepperStep.Persons]: WizardStep.PersonsBasic,
    [StepperStep.AdditionalDocuments]: WizardStep.AdditionalDocuments,
    [StepperStep.Submit]: WizardStep.Submit,
};

// A mapping w/ fields that can allow us to understand whether the step
// was complete or not
export const stepMeaningfulFieldsMap: Record<
    Exclude<WizardStep, WizardStep.UserInfo | WizardStep.Submit>,
    (FormDataFields | ((formData: NonNullable<GetKyc['response']['data']>) => FormDataFields[]))[]
> = {
    // [WizardStep.UserInfo]: ['contactDetails.email'],
    [WizardStep.CompanyInfo]: [
        'businessInformation.companyName',
        'businessInformation.businessCountry',
        'businessInformation.legalEntityType',
        'businessInformation.vatNumber',
        'businessInformation.incorporationDate',
    ],
    [WizardStep.CompanyAddresses]: [
        'businessInformation.registeredAddress.street',
        'businessInformation.registeredAddress.buildingNumber',
        'businessInformation.registeredAddress.buildingName',
        'businessInformation.registeredAddress.city',
        'businessInformation.registeredAddress.postalCode',
        'businessInformation.registeredAddress.country',
        'businessInformation.tradingAddress.street',
        'businessInformation.tradingAddress.buildingNumber',
        'businessInformation.tradingAddress.buildingName',
        'businessInformation.tradingAddress.city',
        'businessInformation.tradingAddress.postalCode',
        'businessInformation.tradingAddress.country',
    ],
    [WizardStep.CompanySector]: [
        'businessInformation.sector',
        (formData: NonNullable<GetKyc['response']['data']>) =>
            formData.businessInformation?.noBusinessWebsite
                ? ['businessInformation.businessActivityDescription', 'businessInformation.documents']
                : ['businessInformation.businessWebsite'],
    ],
    [WizardStep.AccountUsageStep1]: [
        'accountUsage.primaryPurpose',
        'accountUsage.sourceOfFunds',
        'accountUsage.collectionsAccount',
    ],
    [WizardStep.AccountUsageStep2]: [
        'accountUsage.estimatedMonthlyTransactionCurrency',
        'accountUsage.estimatedMonthlyTransactionVolume',
        'accountUsage.estimatedMonthlyTransactionValue',
    ],
    [WizardStep.AccountUsageStep3]: ['accountUsage.transactionCountries'],
    [WizardStep.PersonsBasic]: ['people.persons'],
    // TODO: this should be a unique combination of fields
    [WizardStep.PersonsSettings]: ['people.persons'],
    [WizardStep.PersonsDetails]: ['people.persons'],
    [WizardStep.AdditionalDocuments]: ['additionalDocuments.documents'],
};

export const kycStatusToPageMap = {
    [FormStage.draft]: Path.walletsKycIncomplete,
    [FormStage.approved]: Path.walletsKycApproved,
    [FormStage.rejected]: Path.walletsKycRejected,
    [FormStage.submitted]: Path.walletsKycSubmitted,
};
